type TransportAdmissionResult<TResult> =
	| { status: "superseded" }
	| { status: "settled"; result: TResult };

type TransportHandoff<TResult> = {
	settlement: Promise<TResult>;
};

type TransportOperationScope = {
	attemptHandoff<TResult>(options: {
		prepare: () => void | Promise<void>;
		handOff: () => TransportHandoff<TResult>;
	}): Promise<TResult>;
};

type BarrierGate = {
	state: "pending" | "resolved" | "rejected";
	fallback: BarrierGate | null;
	promise: Promise<void>;
	resolve: () => void;
	reject: (error: unknown) => void;
};

type OrderGate = {
	state: "pending" | "resolved";
	promise: Promise<void>;
	resolve: () => void;
};

type OperationState =
	| "registered"
	| "preparing"
	| "handing-off"
	| "handed-off"
	| "settlement-failed"
	| "handoff-failed"
	| "attempt-settled"
	| "settled";

type PendingOperation = {
	generation: number;
	barrierGate: BarrierGate;
	state: OperationState;
	activeAttempt: boolean;
	attemptCount: number;
	everHandedOff: boolean;
	hasLastSettlementError: boolean;
	lastSettlementError?: unknown;
	superseded: boolean;
	outerSettled: boolean;
	settleAfterBarrier: () => void;
};

const OPERATION_SUPERSEDED = Symbol("operation-superseded");

const createBarrierGate = (fallback: BarrierGate): BarrierGate => {
	let resolvePromise: (() => void) | undefined;
	let rejectPromise: ((error: unknown) => void) | undefined;
	const promise = new Promise<void>((resolve, reject) => {
		resolvePromise = resolve;
		rejectPromise = reject;
	});
	let settled = false;
	const gate: BarrierGate = {
		state: "pending",
		fallback,
		promise,
		resolve: () => {
			if (settled) return;
			settled = true;
			gate.state = "resolved";
			resolvePromise?.();
		},
		reject: (error) => {
			if (settled) return;
			settled = true;
			gate.state = "rejected";
			rejectPromise?.(error);
		},
	};
	return gate;
};

const RESOLVED_BARRIER_GATE: BarrierGate = {
	state: "resolved",
	fallback: null,
	promise: Promise.resolve(),
	resolve: () => {},
	reject: () => {},
};

const createOrderGate = (resolved = false): OrderGate => {
	let resolvePromise: (() => void) | undefined;
	const promise = new Promise<void>((resolve) => {
		resolvePromise = resolve;
	});
	const gate: OrderGate = {
		state: resolved ? "resolved" : "pending",
		promise: resolved ? Promise.resolve() : promise,
		resolve: () => {
			if (gate.state === "resolved") return;
			gate.state = "resolved";
			resolvePromise?.();
		},
	};
	return gate;
};

const isPromiseLike = (value: unknown): value is PromiseLike<void> =>
	typeof value === "object" &&
	value !== null &&
	"then" in value &&
	typeof value.then === "function";

/**
 * Owns caller-generation admission before the SDK hands a command to its host.
 * Handle FIFO, stale classification, and settlement publication remain with the
 * Flow command controller.
 *
 * @see docs/specs/2026-08-02-flow-interaction-model.md
 */
export const createClientTransportAdmission = () => {
	let generation = 0;
	let latestBarrierGate = RESOLVED_BARRIER_GATE;
	let barrierOrderTail = createOrderGate(true);
	const pendingOperations = new Set<PendingOperation>();

	const runOperation = <TResult>(
		execute: (scope: TransportOperationScope) => Promise<TResult>,
	): Promise<TransportAdmissionResult<TResult>> => {
		let resolveBarrierOutcome:
			| ((result: TransportAdmissionResult<TResult>) => void)
			| undefined;
		let rejectBarrierOutcome: ((error: unknown) => void) | undefined;
		const barrierOutcome = new Promise<TransportAdmissionResult<TResult>>(
			(resolve, reject) => {
				resolveBarrierOutcome = resolve;
				rejectBarrierOutcome = reject;
			},
		);
		const operation: PendingOperation = {
			generation,
			barrierGate: latestBarrierGate,
			state: "registered",
			activeAttempt: false,
			attemptCount: 0,
			everHandedOff: false,
			hasLastSettlementError: false,
			superseded: false,
			outerSettled: false,
			settleAfterBarrier: () => {
				if (operation.everHandedOff && operation.hasLastSettlementError) {
					rejectBarrierOutcome?.(operation.lastSettlementError);
					return;
				}
				resolveBarrierOutcome?.({ status: "superseded" });
			},
		};
		pendingOperations.add(operation);

		const throwSuperseded = (): never => {
			if (operation.everHandedOff && operation.hasLastSettlementError) {
				throw operation.lastSettlementError;
			}
			throw OPERATION_SUPERSEDED;
		};
		const assertCurrent = (): void => {
			if (operation.outerSettled) {
				throw new Error("Transport operation has already settled");
			}
			if (operation.superseded || operation.generation !== generation) {
				throwSuperseded();
			}
		};

		const attemptHandoff: TransportOperationScope["attemptHandoff"] = async <
			THandoffResult,
		>({
			prepare,
			handOff,
		}: {
			prepare: () => void | Promise<void>;
			handOff: () => TransportHandoff<THandoffResult>;
		}): Promise<THandoffResult> => {
			assertCurrent();
			if (operation.activeAttempt) {
				throw new Error("A transport handoff attempt is already active");
			}
			if (
				operation.attemptCount > 0 &&
				operation.state !== "settlement-failed"
			) {
				throw new Error(
					"A transport handoff may retry only after a failed settlement",
				);
			}
			if (operation.attemptCount >= 2) {
				throw new Error("A transport operation may retry at most once");
			}

			operation.activeAttempt = true;
			operation.attemptCount += 1;
			operation.state = "preparing";
			try {
				if (operation.barrierGate.state !== "resolved") {
					await operation.barrierGate.promise;
				}
				assertCurrent();
				const preparation = prepare();
				if (isPromiseLike(preparation)) {
					await preparation;
				}
				assertCurrent();
				operation.state = "handing-off";
				let settlement: Promise<THandoffResult>;
				try {
					settlement = handOff().settlement;
				} catch (error) {
					operation.activeAttempt = false;
					operation.state = "handoff-failed";
					if (operation.superseded || operation.generation !== generation) {
						operation.settleAfterBarrier();
					}
					throw error;
				}

				operation.everHandedOff = true;
				operation.state = "handed-off";
				try {
					const result = await settlement;
					operation.activeAttempt = false;
					operation.hasLastSettlementError = false;
					operation.lastSettlementError = undefined;
					operation.state = "attempt-settled";
					return result;
				} catch (error) {
					operation.activeAttempt = false;
					operation.hasLastSettlementError = true;
					operation.lastSettlementError = error;
					operation.state = "settlement-failed";
					if (operation.superseded || operation.generation !== generation) {
						operation.settleAfterBarrier();
					}
					throw error;
				}
			} catch (error) {
				if (operation.activeAttempt) {
					operation.activeAttempt = false;
					operation.state = "handoff-failed";
				}
				if (operation.superseded || operation.generation !== generation) {
					operation.settleAfterBarrier();
					throwSuperseded();
				}
				throw error;
			}
		};

		let internalRun: Promise<TResult>;
		try {
			internalRun = Promise.resolve(execute({ attemptHandoff }));
		} catch (error) {
			internalRun = Promise.reject(error);
		}
		const observedRun: Promise<TransportAdmissionResult<TResult>> =
			internalRun.then(
				(result) => {
					operation.outerSettled = true;
					operation.state = "settled";
					pendingOperations.delete(operation);
					return { status: "settled" as const, result };
				},
				(error) => {
					operation.outerSettled = true;
					operation.state = "settled";
					pendingOperations.delete(operation);
					if (error === OPERATION_SUPERSEDED && !operation.everHandedOff) {
						return { status: "superseded" as const };
					}
					throw error;
				},
			);

		return Promise.race([observedRun, barrierOutcome]);
	};

	const runBarrier = <TResult>({
		onGenerationAdvanced,
		prepare,
		handOff,
	}: {
		onGenerationAdvanced: () => void;
		prepare: () => void | Promise<void>;
		handOff: () => TransportHandoff<TResult>;
	}): Promise<TResult> => {
		generation += 1;
		const previousOrderTail = barrierOrderTail;
		const previousAdmissionGate = latestBarrierGate;
		const currentOrderTail = createOrderGate();
		barrierOrderTail = currentOrderTail;
		const currentGate = createBarrierGate(previousAdmissionGate);
		latestBarrierGate = currentGate;
		void currentGate.promise.catch(() => {});

		for (const operation of pendingOperations) {
			if (operation.generation >= generation) continue;
			operation.superseded = true;
			if (
				operation.state !== "handing-off" &&
				operation.state !== "handed-off" &&
				operation.state !== "attempt-settled"
			) {
				operation.settleAfterBarrier();
			}
		}

		const rejectAdmissionGate = (error: unknown): void => {
			currentGate.reject(error);
			if (latestBarrierGate === currentGate) {
				let fallback = previousAdmissionGate;
				while (fallback.state === "rejected" && fallback.fallback) {
					fallback = fallback.fallback;
				}
				latestBarrierGate = fallback;
			}
		};

		try {
			onGenerationAdvanced();
		} catch (error) {
			rejectAdmissionGate(error);
			if (previousOrderTail.state === "resolved") {
				currentOrderTail.resolve();
			} else {
				void previousOrderTail.promise.then(currentOrderTail.resolve);
			}
			return Promise.reject(error);
		}

		const performHandoff = (): Promise<TResult> => {
			let settlement: Promise<TResult>;
			try {
				settlement = handOff().settlement;
			} catch (error) {
				rejectAdmissionGate(error);
				currentOrderTail.resolve();
				throw error;
			}
			currentGate.fallback = null;
			currentGate.resolve();
			currentOrderTail.resolve();
			return settlement;
		};
		const prepareAndHandOff = (): Promise<TResult> => {
			try {
				const preparation = prepare();
				if (isPromiseLike(preparation)) {
					return Promise.resolve(preparation).then(performHandoff, (error) => {
						rejectAdmissionGate(error);
						currentOrderTail.resolve();
						throw error;
					});
				}
				return performHandoff();
			} catch (error) {
				rejectAdmissionGate(error);
				currentOrderTail.resolve();
				return Promise.reject(error);
			}
		};

		return previousOrderTail.state === "resolved"
			? prepareAndHandOff()
			: previousOrderTail.promise.then(prepareAndHandOff);
	};

	return { runOperation, runBarrier };
};
